import { useState, useEffect } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { Newspaper, Plus, Edit2, Trash2, Eye, EyeOff, Pin, Loader2, Sparkles, Megaphone, Calendar, Wrench, Lightbulb, BarChart3, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { BlockEditor } from "@/components/Tutorials/TutorialBlocks";

const API = `${getApiUrl()}/api`;

const UPDATE_TYPES = [
  { value: "feature", label: "Feature", icon: Sparkles },
  { value: "announcement", label: "Announcement", icon: Megaphone },
  { value: "event", label: "Event", icon: Calendar },
  { value: "maintenance", label: "Maintenance", icon: Wrench },
  { value: "tip", label: "Tip", icon: Lightbulb },
];

const ICON_COLORS = [
  { value: "purple", label: "Purple", class: "bg-purple-500" },
  { value: "blue", label: "Blue", class: "bg-blue-500" },
  { value: "green", label: "Green", class: "bg-green-500" },
  { value: "yellow", label: "Yellow", class: "bg-yellow-500" },
  { value: "red", label: "Red", class: "bg-red-500" },
  { value: "orange", label: "Orange", class: "bg-orange-500" },
];

export default function CommunityUpdatesManager() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    blocks: [],
    updateType: "announcement",
    iconColor: "purple",
    isPublished: true,
    isPinned: false,
    sortOrder: 0,
    publishDate: "",
    sendEmail: true,
    appendSignature: true,
    poll: null,
  });

  const [publishMode, setPublishMode] = useState("now"); // now | schedule | draft
  const [scheduleLocal, setScheduleLocal] = useState(""); // "YYYY-MM-DDTHH:MM"
  const [scheduleTz, setScheduleTz] = useState("America/Los_Angeles");
  const [sendingTest, setSendingTest] = useState(false);
  // Per-admin signature
  const emptySig = { name: "", title: "", tagline: "", linkLabel: "", linkUrl: "" };
  const [signature, setSignature] = useState(emptySig);
  const [savingSig, setSavingSig] = useState(false);
  const sigHasContent = (s) => !!s && ["name", "title", "tagline", "linkLabel", "linkUrl"].some((k) => (s[k] || "").trim());

  const TZ_LABELS = {
    "America/Los_Angeles": "Pacific Time (PT)",
    "America/New_York": "Eastern Time (ET)",
    "America/Chicago": "Central Time (CT)",
    "America/Denver": "Mountain Time (MT)",
    "America/Anchorage": "Alaska Time (AKT)",
    "Pacific/Honolulu": "Hawaii Time (HT)",
    "Europe/London": "London (GMT/BST)",
    "Europe/Paris": "Central European (CET)",
    "Europe/Moscow": "Moscow (MSK)",
    "Asia/Tokyo": "Japan (JST)",
    "Asia/Shanghai": "China (CST)",
    "UTC": "UTC",
  };

  const utcIsoToLocalInput = (iso, tz) => {
    try {
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: false,
      }).formatToParts(new Date(iso));
      const g = (t) => parts.find((p) => p.type === t)?.value;
      let hh = g("hour");
      if (hh === "24") hh = "00";
      return `${g("year")}-${g("month")}-${g("day")}T${hh}:${g("minute")}`;
    } catch {
      return "";
    }
  };

  const fmtScheduled = (iso) => {
    try {
      return (
        new Date(iso).toLocaleString("en-US", { timeZone: scheduleTz, dateStyle: "medium", timeStyle: "short" }) +
        ` (${TZ_LABELS[scheduleTz] || scheduleTz})`
      );
    } catch {
      return iso;
    }
  };

  useEffect(() => {
    fetchUpdates();
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/timezone`);
        const tz = res.data?.timezone;
        if (tz && tz !== "UTC") setScheduleTz(tz);
      } catch { /* keep PT default */ }
    })();
    (async () => {
      try {
        const res = await axios.get(`${API}/community/admin/signature`);
        setSignature({ ...emptySig, ...(res.data || {}) });
      } catch { /* ignore */ }
    })();
  }, []);

  const saveSignature = async () => {
    setSavingSig(true);
    try {
      await axios.put(`${API}/community/admin/signature`, signature);
      toast.success("Signature saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save signature");
    } finally {
      setSavingSig(false);
    }
  };

  const fetchUpdates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/community/admin/updates`);
      setUpdates(response.data || []);
    } catch (error) {
      console.error("Failed to fetch updates:", error);
      toast.error("Failed to load updates");
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingUpdate(null);
    setFormData({
      title: "",
      content: "",
      blocks: [],
      updateType: "announcement",
      iconColor: "purple",
      isPublished: true,
      isPinned: false,
      sortOrder: 0,
      publishDate: new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" }),
      sendEmail: true,
      appendSignature: true,
      poll: null,
    });
    setPublishMode("now");
    setScheduleLocal("");
    setEditDialogOpen(true);
  };

  const openEditDialog = (update) => {
    setEditingUpdate(update);
    setFormData({
      title: update.title,
      content: update.content || "",
      blocks: Array.isArray(update.blocks) ? update.blocks : [],
      updateType: update.updateType,
      iconColor: update.iconColor,
      isPublished: update.isPublished,
      isPinned: update.isPinned,
      sortOrder: update.sortOrder || 0,
      publishDate: update.publishDate || "",
      sendEmail: update.sendEmail !== false,
      appendSignature: update.appendSignature !== false,
      poll: update.poll ? {
        question: update.poll.question || "",
        options: (update.poll.options || []).map((o) => ({ id: o.id, text: o.text })),
        maxSelections: update.poll.maxSelections || 1,
        endsAtLocal: update.poll.endsAt ? utcIsoToLocalInput(update.poll.endsAt, scheduleTz) : "",
        resultsVisibility: update.poll.resultsVisibility || "afterVote",
        announceResults: update.poll.announceResults !== false,
      } : null,
    });
    if (update.scheduledFor) {
      setPublishMode("schedule");
      setScheduleLocal(utcIsoToLocalInput(update.scheduledFor, scheduleTz));
    } else {
      setPublishMode(update.isPublished ? "now" : "draft");
      setScheduleLocal("");
    }
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim() || (!formData.content.trim() && (formData.blocks || []).length === 0)) {
      toast.error("Add a title and either a summary or some body content");
      return;
    }

    let payload = { ...formData };
    if (publishMode === "schedule") {
      if (!scheduleLocal) {
        toast.error("Pick a date and time to schedule this post");
        return;
      }
      const when = new Date(scheduleLocal);
      if (!editingUpdate && when.getTime() <= Date.now()) {
        toast.error("Choose a future date and time");
        return;
      }
      payload = { ...payload, isPublished: false, scheduledFor: scheduleLocal, scheduledTimezone: scheduleTz };
    } else if (publishMode === "draft") {
      payload = { ...payload, isPublished: false, scheduledFor: null };
    } else {
      payload = { ...payload, isPublished: true, scheduledFor: null };
    }

    // Poll: validate + shape for the API (or clear it).
    if (formData.poll) {
      const p = formData.poll;
      const opts = (p.options || []).map((o) => ({ ...o, text: (o.text || "").trim() })).filter((o) => o.text);
      if (!p.question.trim()) { toast.error("Your poll needs a question"); return; }
      if (opts.length < 2) { toast.error("Your poll needs at least 2 non-empty options"); return; }
      if (opts.length > 8) { toast.error("A poll can have at most 8 options"); return; }
      if (!p.endsAtLocal) { toast.error("Set a date & time for when the poll closes"); return; }
      payload.poll = {
        question: p.question.trim(),
        options: opts,
        maxSelections: Math.min(p.maxSelections || 1, opts.length),
        endsAt: p.endsAtLocal,
        endsAtTimezone: scheduleTz,
        resultsVisibility: p.resultsVisibility || "afterVote",
        announceResults: p.announceResults !== false,
      };
    } else {
      payload.poll = null;
    }

    setSaving(true);
    try {
      if (editingUpdate) {
        await axios.put(`${API}/community/admin/updates/${editingUpdate.id}`, payload);
        toast.success(publishMode === "schedule" ? "Post scheduled" : "Update modified");
      } else {
        await axios.post(`${API}/community/admin/updates`, payload);
        toast.success(publishMode === "schedule" ? "Post scheduled" : "Update created");
      }
      setEditDialogOpen(false);
      fetchUpdates();
    } catch (error) {
      console.error("Failed to save update:", error);
      toast.error(error.response?.data?.detail || "Failed to save update");
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!formData.title.trim() && !formData.content.trim() && (formData.blocks || []).length === 0) {
      toast.error("Add a title or some content first");
      return;
    }
    setSendingTest(true);
    try {
      const res = await axios.post(`${API}/community/admin/updates/test-email`, {
        title: formData.title,
        content: formData.content,
        blocks: formData.blocks || [],
      });
      toast.success(`Test email sent to ${res.data?.email || "your inbox"}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  // ---- Poll builder helpers ----
  const enablePoll = () => setFormData((fd) => ({
    ...fd,
    poll: {
      question: "",
      options: [{ id: crypto.randomUUID(), text: "" }, { id: crypto.randomUUID(), text: "" }],
      maxSelections: 1,
      endsAtLocal: "",
      resultsVisibility: "afterVote",
      announceResults: true,
    },
  }));
  const disablePoll = () => setFormData((fd) => ({ ...fd, poll: null }));
  const setPoll = (patch) => setFormData((fd) => ({ ...fd, poll: { ...fd.poll, ...patch } }));
  const addPollOption = () => setFormData((fd) => {
    if (!fd.poll || fd.poll.options.length >= 8) return fd;
    return { ...fd, poll: { ...fd.poll, options: [...fd.poll.options, { id: crypto.randomUUID(), text: "" }] } };
  });
  const removePollOption = (id) => setFormData((fd) => {
    if (!fd.poll || fd.poll.options.length <= 2) return fd;
    const options = fd.poll.options.filter((o) => o.id !== id);
    const maxSelections = Math.min(fd.poll.maxSelections || 1, options.length);
    return { ...fd, poll: { ...fd.poll, options, maxSelections } };
  });
  const updatePollOption = (id, text) => setFormData((fd) => ({
    ...fd,
    poll: { ...fd.poll, options: fd.poll.options.map((o) => (o.id === id ? { ...o, text } : o)) },
  }));


  const handleDelete = async (updateId) => {
    if (!confirm("Are you sure you want to delete this update?")) return;
    
    try {
      await axios.delete(`${API}/community/admin/updates/${updateId}`);
      toast.success("Update deleted");
      fetchUpdates();
    } catch (error) {
      console.error("Failed to delete update:", error);
      toast.error("Failed to delete update");
    }
  };

  const togglePublished = async (update) => {
    try {
      const nextPublished = !update.isPublished;
      const payload = { isPublished: nextPublished };
      if (nextPublished && update.scheduledFor) payload.scheduledFor = null;
      await axios.put(`${API}/community/admin/updates/${update.id}`, payload);
      toast.success(update.isPublished ? "Update hidden" : "Update published");
      fetchUpdates();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const togglePinned = async (update) => {
    try {
      await axios.put(`${API}/community/admin/updates/${update.id}`, {
        isPinned: !update.isPinned
      });
      toast.success(update.isPinned ? "Update unpinned" : "Update pinned");
      fetchUpdates();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const getTypeIcon = (type) => {
    const found = UPDATE_TYPES.find(t => t.value === type);
    return found ? found.icon : Newspaper;
  };

  const getColorClass = (color) => {
    const found = ICON_COLORS.find(c => c.value === color);
    return found ? found.class : "bg-purple-500";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-white flex items-center gap-2">
          <Newspaper className="w-5 h-5 text-purple-400" />
          Community Updates
        </h2>
        <Button onClick={openCreateDialog} className="bg-purple-600 hover:bg-purple-500">
          <Plus className="w-4 h-4 mr-2" />
          New Update
        </Button>
      </div>

      {/* My Signature — auto-appended to this admin's news posts */}
      <Card className="bg-[#121212] border-white/5" data-testid="signature-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Edit2 className="w-4 h-4 text-purple-400" />
            My Signature
          </CardTitle>
          <p className="text-white/40 text-xs">Automatically added to the bottom of news posts you publish (toggle per post). Editing this won't change posts you've already published.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-white/60">Name</Label>
              <Input value={signature.name} onChange={(e) => setSignature({ ...signature, name: e.target.value })} placeholder="Your name" className="bg-[#0A0A0A] border-white/10" data-testid="signature-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-white/60">Title / Role</Label>
              <Input value={signature.title} onChange={(e) => setSignature({ ...signature, title: e.target.value })} placeholder="e.g. Community Admin" className="bg-[#0A0A0A] border-white/10" data-testid="signature-title" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-white/60">Tagline (optional)</Label>
            <Input value={signature.tagline} onChange={(e) => setSignature({ ...signature, tagline: e.target.value })} placeholder="A short line under your name" className="bg-[#0A0A0A] border-white/10" data-testid="signature-tagline" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-white/60">Link label (optional)</Label>
              <Input value={signature.linkLabel} onChange={(e) => setSignature({ ...signature, linkLabel: e.target.value })} placeholder="e.g. Follow me" className="bg-[#0A0A0A] border-white/10" data-testid="signature-link-label" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-white/60">Link URL (optional)</Label>
              <Input value={signature.linkUrl} onChange={(e) => setSignature({ ...signature, linkUrl: e.target.value })} placeholder="https://..." className="bg-[#0A0A0A] border-white/10" data-testid="signature-link-url" />
            </div>
          </div>
          {sigHasContent(signature) && (
            <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-white/50" data-testid="signature-preview">
              <p className="text-[10px] uppercase tracking-wide text-white/30 mb-1">Preview</p>
              <p className="text-sm italic">— {signature.name}{signature.title ? <span className="text-white/40">, {signature.title}</span> : null}</p>
              {signature.tagline && <p className="text-xs text-white/40 mt-0.5">{signature.tagline}</p>}
              {signature.linkUrl && <span className="text-xs text-purple-400 underline mt-0.5 inline-block">{signature.linkLabel || signature.linkUrl}</span>}
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={saveSignature} disabled={savingSig} className="bg-purple-600 hover:bg-purple-500" data-testid="signature-save-btn">
              {savingSig ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Signature
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
        </div>
      ) : updates.length === 0 ? (
        <Card className="bg-[#121212] border-white/5">
          <CardContent className="py-12 text-center">
            <Newspaper className="w-12 h-12 mx-auto mb-4 text-white/20" />
            <p className="text-white/50">No updates yet</p>
            <Button onClick={openCreateDialog} variant="link" className="text-purple-400 mt-2">
              Create your first update
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {updates.map((update) => {
            const IconComponent = getTypeIcon(update.updateType);
            
            return (
              <Card 
                key={update.id} 
                className={`bg-[#121212] border-white/5 ${!update.isPublished ? "opacity-50" : ""}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${getColorClass(update.iconColor)}/20`}>
                      <IconComponent className={`w-5 h-5 ${getColorClass(update.iconColor).replace("bg-", "text-").replace("-500", "-400")}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-white font-medium">{update.title}</h3>
                        {update.isPinned && (
                          <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 flex items-center gap-1">
                            <Pin className="w-3 h-3" /> Pinned
                          </span>
                        )}
                        {!update.isPublished && update.scheduledFor && (
                          <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 flex items-center gap-1" data-testid={`news-scheduled-badge-${update.id}`}>
                            <Calendar className="w-3 h-3" /> Scheduled
                          </span>
                        )}
                        {!update.isPublished && !update.scheduledFor && (
                          <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-300">Draft</span>
                        )}
                      </div>
                      <p className="text-white/60 text-sm mt-1 line-clamp-2">{update.content}</p>
                      {update.scheduledFor ? (
                        <p className="text-cyan-300/80 text-xs mt-2 flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> Publishes {fmtScheduled(update.scheduledFor)}
                        </p>
                      ) : (
                        <p className="text-white/40 text-xs mt-2">{update.publishDate}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => togglePinned(update)}
                        title={update.isPinned ? "Unpin" : "Pin"}
                        className={update.isPinned ? "text-purple-400" : "text-white/40"}
                      >
                        <Pin className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => togglePublished(update)}
                        title={update.isPublished ? "Hide" : "Publish"}
                        className={update.isPublished ? "text-green-400" : "text-white/40"}
                      >
                        {update.isPublished ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(update)}
                        className="text-white/40 hover:text-white"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(update.id)}
                        className="text-white/40 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUpdate ? "Edit Update" : "Create Update"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Update title..."
                className="bg-[#121212] border-white/10"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Summary <span className="text-white/40 font-normal">(short intro shown at the top of the post)</span></Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="A short summary or intro line..."
                className="bg-[#121212] border-white/10 min-h-[70px]"
              />
            </div>

            <div className="space-y-2">
              <Label>Body <span className="text-white/40 font-normal">(rich text — bold, italics, headings, lists, links, images & video)</span></Label>
              <BlockEditor
                blocks={formData.blocks}
                onChange={(blocks) => setFormData({ ...formData, blocks })}
              />
            </div>

            {/* Poll builder */}
            <div className="space-y-3 p-3 bg-[#121212] border border-white/10 rounded-lg" data-testid="news-poll-section">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-white">
                  <BarChart3 className="w-4 h-4 text-purple-400" /> Poll
                  <span className="text-white/40 font-normal text-xs">(optional)</span>
                </Label>
                {!formData.poll ? (
                  <Button type="button" size="sm" variant="outline" onClick={enablePoll} className="h-7 text-xs border-purple-500/40 text-purple-300 hover:bg-purple-500/10" data-testid="news-poll-add">
                    <Plus className="w-3 h-3 mr-1" /> Add a poll
                  </Button>
                ) : (
                  <Button type="button" size="sm" variant="ghost" onClick={disablePoll} className="h-7 text-xs text-white/50 hover:text-red-300" data-testid="news-poll-remove">
                    <Trash2 className="w-3 h-3 mr-1" /> Remove poll
                  </Button>
                )}
              </div>

              {formData.poll && (
                <div className="space-y-3">
                  <Input
                    value={formData.poll.question}
                    onChange={(e) => setPoll({ question: e.target.value })}
                    placeholder="Poll question — e.g., Which game should we feature next?"
                    className="bg-[#0A0A0A] border-white/10"
                    data-testid="news-poll-question"
                  />

                  <div className="space-y-2">
                    <Label className="text-white/70 text-xs">Options ({formData.poll.options.length}/8)</Label>
                    {formData.poll.options.map((opt, i) => (
                      <div key={opt.id} className="flex items-center gap-2">
                        <Input
                          value={opt.text}
                          onChange={(e) => updatePollOption(opt.id, e.target.value)}
                          placeholder={`Option ${i + 1}`}
                          className="bg-[#0A0A0A] border-white/10 flex-1"
                          data-testid={`news-poll-option-${i}`}
                        />
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => removePollOption(opt.id)}
                          disabled={formData.poll.options.length <= 2}
                          className="h-9 w-9 shrink-0 text-white/40 hover:text-red-300 disabled:opacity-30"
                          data-testid={`news-poll-option-remove-${i}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    {formData.poll.options.length < 8 && (
                      <Button type="button" size="sm" variant="outline" onClick={addPollOption} className="h-7 text-xs border-white/10" data-testid="news-poll-option-add">
                        <Plus className="w-3 h-3 mr-1" /> Add option
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white/70 text-xs">Selections allowed</Label>
                      <Select value={String(formData.poll.maxSelections)} onValueChange={(v) => setPoll({ maxSelections: parseInt(v, 10) })}>
                        <SelectTrigger className="bg-[#0A0A0A] border-white/10 h-9" data-testid="news-poll-maxselections">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10">
                          {Array.from({ length: formData.poll.options.length }, (_, i) => i + 1).map((n) => (
                            <SelectItem key={n} value={String(n)}>{n === 1 ? "Pick 1 (single choice)" : `Pick up to ${n}`}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70 text-xs">Show results</Label>
                      <Select value={formData.poll.resultsVisibility} onValueChange={(v) => setPoll({ resultsVisibility: v })}>
                        <SelectTrigger className="bg-[#0A0A0A] border-white/10 h-9" data-testid="news-poll-visibility">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10">
                          <SelectItem value="afterVote">After the user votes</SelectItem>
                          <SelectItem value="afterClose">Only after the poll closes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-white/70 text-xs flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-purple-400" /> Voting closes
                    </Label>
                    <Input
                      type="datetime-local"
                      value={formData.poll.endsAtLocal}
                      onChange={(e) => setPoll({ endsAtLocal: e.target.value })}
                      className="bg-[#0A0A0A] border-white/10 [color-scheme:dark]"
                      data-testid="news-poll-endsat"
                    />
                    <p className="text-white/40 text-xs">
                      Times are in {TZ_LABELS[scheduleTz] || scheduleTz}. Users can change their vote until then, after which votes lock.
                    </p>
                  </div>

                  <div className="flex items-start justify-between gap-3 pt-1">
                    <div className="flex-1">
                      <Label className="text-white text-sm">Announce the winner when it closes</Label>
                      <p className="text-white/40 text-xs mt-0.5">
                        Auto-post a "Poll Results" news item with the winner and tallies once voting ends.
                      </p>
                    </div>
                    <Switch
                      checked={formData.poll.announceResults !== false}
                      onCheckedChange={(v) => setPoll({ announceResults: v })}
                      className="data-[state=checked]:bg-purple-600"
                      data-testid="news-poll-announce"
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={formData.updateType} onValueChange={(v) => setFormData({ ...formData, updateType: v })}>
                  <SelectTrigger className="bg-[#121212] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {UPDATE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Icon Color</Label>
                <Select value={formData.iconColor} onValueChange={(v) => setFormData({ ...formData, iconColor: v })}>
                  <SelectTrigger className="bg-[#121212] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {ICON_COLORS.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${color.class}`} />
                          {color.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Display Date</Label>
              <Input
                value={formData.publishDate}
                onChange={(e) => setFormData({ ...formData, publishDate: e.target.value })}
                placeholder="e.g., February 2026"
                className="bg-[#121212] border-white/10"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Publish</Label>
                <Select value={publishMode} onValueChange={setPublishMode}>
                  <SelectTrigger className="bg-[#121212] border-white/10" data-testid="news-publish-mode-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="now" data-testid="news-publish-now">Publish now</SelectItem>
                    <SelectItem value="schedule" data-testid="news-publish-schedule">Schedule for later</SelectItem>
                    <SelectItem value="draft" data-testid="news-publish-draft">Save as draft (hidden)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-3 bg-[#121212] rounded-lg self-end">
                <Label className="cursor-pointer">Pinned</Label>
                <Switch
                  checked={formData.isPinned}
                  onCheckedChange={(v) => setFormData({ ...formData, isPinned: v })}
                />
              </div>
            </div>

            {publishMode === "schedule" && (
              <div className="space-y-2 p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-lg" data-testid="news-schedule-row">
                <Label className="flex items-center gap-2 text-white">
                  <Calendar className="w-4 h-4 text-cyan-400" /> Publish date &amp; time
                </Label>
                <Input
                  type="datetime-local"
                  value={scheduleLocal}
                  onChange={(e) => setScheduleLocal(e.target.value)}
                  className="bg-[#121212] border-white/10 [color-scheme:dark]"
                  data-testid="news-schedule-input"
                />
                <p className="text-white/40 text-xs">
                  Times are in {TZ_LABELS[scheduleTz] || scheduleTz}. The post stays hidden until then, then auto-publishes{formData.sendEmail ? " and emails subscribers" : ""}. (Change your zone in Settings.)
                </p>
              </div>
            )}

            <div className="flex items-start justify-between p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg" data-testid="news-send-email-row">
              <div className="flex-1 pr-3">
                <Label className="cursor-pointer text-white">Email subscribers about this post</Label>
                <p className="text-white/40 text-xs mt-1">
                  {editingUpdate?.newsletterSentAt
                    ? "A newsletter email was already sent for this post — editing won't resend it."
                    : "When published, sends a newsletter email to all subscribed members with a verified address."}
                </p>
              </div>
              <Switch
                data-testid="news-send-email-toggle"
                checked={formData.sendEmail}
                disabled={!!editingUpdate?.newsletterSentAt}
                onCheckedChange={(v) => setFormData({ ...formData, sendEmail: v })}
                className="data-[state=checked]:bg-purple-600"
              />
            </div>

            <div className="flex items-start justify-between p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg" data-testid="news-append-signature-row">
              <div className="flex-1 pr-3">
                <Label className="cursor-pointer text-white">Append my signature</Label>
                <p className="text-white/40 text-xs mt-1">
                  {sigHasContent(signature)
                    ? "Adds your signature to the bottom of this post."
                    : "Set your signature above to use this. (Nothing will be added until you do.)"}
                </p>
              </div>
              <Switch
                data-testid="news-append-signature-toggle"
                checked={formData.appendSignature}
                onCheckedChange={(v) => setFormData({ ...formData, appendSignature: v })}
                className="data-[state=checked]:bg-purple-600"
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2">
            <Button
              variant="outline"
              onClick={handleSendTest}
              disabled={sendingTest}
              data-testid="news-send-test-btn"
              className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10"
            >
              {sendingTest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              Send test email to me
            </Button>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                data-testid="news-save-btn"
                className="bg-purple-600 hover:bg-purple-500"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {publishMode === "schedule"
                  ? (editingUpdate ? "Save Schedule" : "Schedule Post")
                  : (editingUpdate ? "Save Changes" : "Create Update")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
